/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: admin
 *  创建时间: 2026-09-21
 *  文件名: src/components/ppt-learning-plan-viewer/word-editor/office-editor-channel.handlers.ts
 *  功能描述: EuroOffice iframe 调用处理器
 */
import { getToken } from '../../../util';
import {
  InvokeMessage,
  InvokeResultMessage,
  MESSAGE_BRIDGE_CHANNEL,
  OfficeEditorChannelOptions,
} from './office-editor-channel.methods';

const GET_AUTHORIZATION_METHOD = 'getAuthorization';

type OfficeEditorMessageHandler = (params?: unknown) => unknown | Promise<unknown>;

interface InvokeResultPayload {
  result?: unknown;
  error?: string;
}

/** 获取编辑器请求使用的认证信息。 */
function getAuthorization(): string {
  const token = getToken();
  if (!token) throw new Error('未获取到认证 token');
  return `Bearer ${token}`;
}

const MESSAGE_HANDLERS: Record<string, OfficeEditorMessageHandler> = {
  [GET_AUTHORIZATION_METHOD]: getAuthorization,
};

/** 判断 iframe 消息是否为 EuroOffice 方法调用。 */
function isInvokeMessage(message: unknown): message is InvokeMessage {
  if (!message || typeof message !== 'object') return false;

  const invokeMessage = message as Partial<InvokeMessage>;
  return (
    invokeMessage.channel === MESSAGE_BRIDGE_CHANNEL &&
    invokeMessage.type === 'invoke' &&
    typeof invokeMessage.method === 'string'
  );
}

/** 将未知异常转换为可跨窗口传递的错误信息。 */
function getErrorMessage(error: unknown): string {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return errorMessage || '调用执行失败';
}

/** 向 EuroOffice iframe 返回方法调用结果。 */
function postInvokeResult(
  options: OfficeEditorChannelOptions,
  requestId: string,
  payload: InvokeResultPayload,
): void {
  const resultMessage: InvokeResultMessage = {
    channel: MESSAGE_BRIDGE_CHANNEL,
    type: 'invokeResult',
    requestId,
    ...payload,
  };
  options.editorWindow.postMessage(resultMessage, options.editorOrigin);
}

/** 监听 EuroOffice iframe 消息，并返回取消监听函数。 */
function listenOfficeEditorChannel(options: OfficeEditorChannelOptions): () => void {
  const { editorWindow, editorOrigin } = options;

  /** 校验、分发子窗口调用并在需要时返回结果。 */
  const handleMessage = async (event: MessageEvent) => {
    if (event.source !== editorWindow || event.origin !== editorOrigin) return;
    if (!isInvokeMessage(event.data)) return;

    const messageHandler = MESSAGE_HANDLERS[event.data.method];
    if (!messageHandler) return;

    const { requestId } = event.data;
    try {
      const result = await messageHandler(event.data.params);
      if (requestId) postInvokeResult(options, requestId, { result });
    } catch (error) {
      if (requestId) postInvokeResult(options, requestId, { error: getErrorMessage(error) });
    }
  };

  window.addEventListener('message', handleMessage);

  /** 移除当前消息通道监听。 */
  return () => window.removeEventListener('message', handleMessage);
}

export { listenOfficeEditorChannel };
